import {gql, useQuery} from '@apollo/client';
import React, {useState} from 'react';
import {LoadingSpinner, Paths, RelayConnection, SimpleDropdown, ErrorDisplay} from "../../common";
import {ParentCaseInfo, ParentCaseInfoColumn, ParentCaseSortBy, SortOrder} from "./domain";
import {RelayPagination, RelayPaginationState, useRelayPagination} from "../pagination";
import {CaretDownIcon, CaretUpIcon, Icon, Pane} from "evergreen-ui";
import {ParentCasePreview} from "./ParentCasePreview";
import {asQueryParam, ParentCaseListFilter, ParentCaseListFilterValue} from "./ParentCaseListFilter";
import {ParentCaseListAction} from "./parentCaseListReducer";
import {ParentCaseListFilterBadge} from "./ParentCaseListFilterBadge";
import {NavigationSearchBar} from "../../placement/components/NavigationSearchBar";

import {ExportButton} from "common/component/ExportButton";
import {useObservable} from "rxsupport";
import {useExportData} from "./export/useExportData"
import {ExportRequestModal} from "./export/ExportRequestModal";

export const PARENT_CASE_LIST = gql`
query parentCaseQuery($first: Int = 20, $after: String, $params: ParentCaseQueryParamsInput!, $search: String) {
    parentCaseQuery(
        first: $first
        after: $after
        params: $params
        search: $search
    ) {
        edges {
            cursor
            node {
                guid
                caseNumber
                recordType
                status
                clientMrn
                clientCity
                clientGuid
                activeStatus
                isAtRisk
                client {
                    email
                    fullName
                    age
                }
                serviceTypes
                placementDate
            }
        }
        totalElements
        pageInfo {
            hasPreviousPage
            endCursor
            hasNextPage
            startCursor
        }
    }
}
 `

const EXPORT_PARENT_CASES = gql`
    mutation ($filters: String) {
        exportParentCases(filters: $filters) {
            guid
        }
    }
`;

export type ParentCaseQueryResponse = RelayConnection<ParentCaseInfo> & { totalElements: number };

export const ParentCaseColumns: Readonly<ParentCaseInfoColumn[]> = [
    {name: 'Case Number', attr: 'caseNumber'},
    {name: 'Client Name', attr: 'client.fullName'},
    {name: 'Medical Record Number', attr: 'clientMrn'},
    {name: 'Age', attr: 'client.age'},
    {name: 'City', attr: 'clientCity'},
    {name: 'Service Types', attr: 'serviceTypes'},
    {name: 'Placement Date', attr: 'placementDate'},
    {name: 'Status', attr: 'activeStatus'},
]

type ParentCaseListParams = {
    selectedParentCase: ParentCaseInfo | null;
    sortBy: ParentCaseSortBy;
    pagination: RelayPaginationState;
    filterValue: ParentCaseListFilterValue,
    dispatch: React.Dispatch<ParentCaseListAction>;
    searchText: string;
    pageSize: number;
}

export const ParentCaseList = ({
                                   selectedParentCase,
                                   sortBy,
                                   pagination,
                                   dispatch,
                                   filterValue,
                                   searchText,
                                   pageSize,
                               }: ParentCaseListParams) => {

    const {isRunning$: isExporting$, execute: exportData} = useExportData(EXPORT_PARENT_CASES);
    const isExporting = useObservable(isExporting$, false);
    const [exportGuid, setExportGuid] = useState<string | null>(null);
    // Export the current filtered view — sends the same filter snapshot the grid renders, then
    // opens the progress modal to poll for and download the generated file.
    const handleExport = async () => {
        const guid = await exportData(JSON.stringify({
            ...asQueryParam(filterValue),
            search: searchText,
            sortDir: sortBy.order,
            sortBy: sortBy.column
        }));
        if (guid) {
            setExportGuid(guid);
        }
    };

    const {loading, data, error} = useQuery<{ parentCaseQuery: ParentCaseQueryResponse }, {}>(
        PARENT_CASE_LIST, {
            variables: {
                ...pagination.toGraphQlParams(),
                search: searchText,
                params: {
                    ...asQueryParam(filterValue),
                    sortDir: sortBy.order,
                    sortBy: sortBy.column,
                },
            }
        }
    );
    const setSortByColumn = (newSortColumn: Paths<ParentCaseInfo>) => {
        // ignore sort by service types, case provider agency status
        if (['serviceTypes', 'currentCaseProviderAgency.status'].includes(newSortColumn)) {
            return;
        }

        let newSortOrder = SortOrder.DESC;
        if (newSortColumn === sortBy.column && sortBy.order === newSortOrder) {
            newSortOrder = SortOrder.ASC;
        }

        dispatch({type: 'SET_SORT_BY', sortBy: {column: newSortColumn, order: newSortOrder}});
    }

    let totalClients: number = 0;
    if (data?.parentCaseQuery?.pageInfo?.endCursor !== null) {
        totalClients = data?.parentCaseQuery?.totalElements ?? 0;
    }
    const {onPaginationClick} = useRelayPagination(dispatch, totalClients);

    if (error) {
        return <ErrorDisplay error={error} title="Error loading parent cases" />;
    }

    return (
        <Pane className="h-full pt-5 m-5 bg-white shadow-md flex flex-col"
              minHeight="calc(100vh - 100px - 260px - 40px)">
            <Pane display='flex' justifyContent='flex-end' flex="0 0" paddingBottom={20}>
                <div className="flex">

                    

                    <NavigationSearchBar placeholder={"Search by Client Name or MRN"}
                                         searchQuery={searchText}
                                         setSearchQuery={(search) => dispatch({
                                             type: 'SET_SEARCH',
                                             search: search
                                         })}/>
                    <ParentCaseListFilter initFilterValue={filterValue} dispatch={dispatch}/>

                    <div className="mr-3 flex items-center">
                        <ExportButton
                            onClick={handleExport}
                            disabled={isExporting}
                            minWidth="7rem"
                        />
                        <ExportRequestModal guid={exportGuid} onClose={() => setExportGuid(null)}/>
                    </div>

                    <SimpleDropdown options={["10", "50", "100"]} selected={pageSize.toString()}
                                    minWidth={"2rem"}
                                    textColor={"gray-700"}
                                    setSelected={(newPageSize) => dispatch({
                                        type: "SET_PAGE_SIZE",
                                        pageSize: parseInt(newPageSize)
                                    })}/>

                    

                    <RelayPagination pageInfo={data?.parentCaseQuery.pageInfo} count={totalClients}
                                     onClick={onPaginationClick}/>
                    
                </div>
            </Pane>
            <ParentCaseListFilterBadge filter={filterValue} dispatch={dispatch}/>
            <div className="overflow-auto flex flex-col flex-grow mb-4 scrollbar-always">
                <table className="table-fixed w-full">
                    <thead>
                    <tr className='text-left select-none text-sm text-gray-portal-table-title'>
                        {
                            ParentCaseColumns.map((column) => (
                                <th
                                    key={column.attr}
                                    className={'font-light' + (column.attr === 'caseNumber' ? ' pl-8' : '')}
                                    onClick={() => setSortByColumn(column.attr)}
                                >
                                    <div className='flex items-center'>
                                        <div>
                                            {column.name}
                                        </div>
                                        <div className='w-6 h-6 pl-2'>
                                            {sortBy.column === column.attr && sortBy.order === SortOrder.ASC &&
                                                <Icon className='align-middle' icon={CaretUpIcon}/>}
                                            {sortBy.column === column.attr && sortBy.order === SortOrder.DESC &&
                                                <Icon className='align-middle' icon={CaretDownIcon}/>}
                                        </div>
                                    </div>
                                </th>
                            ))
                        }
                    </tr>
                    </thead>
                    <tbody>
                    {
                        !loading && data!.parentCaseQuery.edges.map((edge) => edge.node).map((parentCase) => (
                            <ParentCasePreview
                                key={parentCase.guid}
                                parentCase={parentCase}
                                selected={selectedParentCase?.guid === parentCase.guid}
                                dispatch={dispatch}
                            />
                        ))
                    }
                    </tbody>
                </table>
                {
                    loading &&
                    <div className="w-full h-full flex flex-grow items-center">
                        <LoadingSpinner spacing={20} height={"100%"}/>
                    </div>
                }
                {
                    totalClients === 0 && !loading && <div className="w-full h-full flex flex-grow items-center">
                        <div className="m-auto">
                            No cases matched the searching criteria.
                        </div>
                    </div>
                }
            </div>
        </Pane>
    );
}
